import { useEffect, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import type { Ms365ConnectionLevel } from "./use-connections";

/** Authenticated account authorization, separate from public IT consent links. */
export function ms365AuthorizationBody(
  userEmail?: string,
  scopes?: string[],
  connectionLevel?: Ms365ConnectionLevel,
) {
  return {
    user_email: userEmail,
    ...(scopes && scopes.length > 0 ? { scopes } : {}),
    ...(connectionLevel ? { connection_level: connectionLevel } : {}),
  };
}

export type OnboardingStatus =
  | "loading"
  | "token_invalid"
  | "token_expired"
  | "admin_consent_pending"
  | "admin_consent_done"
  | "admin_consent_error"
  | "fully_complete";

export interface OnboardingData {
  tenant_id: string;
  customer_name: string;
  admin_consent_url: string | null;
  user_auth_url: string | null;
  admin_consent_granted: boolean;
  active_connections: number;
  /** Feature/effective scopes selected when this shareable link was minted. */
  requested_scopes?: string[];
  connections: Array<{
    user_oid: string;
    user_email?: string;
    status: string;
    scopes: string[];
  }>;
}

export interface UseMs365OnboardingReturn {
  status: OnboardingStatus;
  data: OnboardingData | null;
  error: string | null;
  refresh: () => Promise<void>;
}

export function useMs365Onboarding(): UseMs365OnboardingReturn {
  const { t } = useTranslation("connections");
  const [status, setStatus] = useState<OnboardingStatus>("loading");
  const [data, setData] = useState<OnboardingData | null>(null);
  const [error, setError] = useState<string | null>(null);

  const token = new URLSearchParams(window.location.search).get("token");

  const refresh = useCallback(async () => {
    if (!token) {
      setStatus("token_invalid");
      setError(t("ms365Onboarding.errors.noToken"));
      return;
    }

    setStatus("loading");
    setError(null);

    try {
      const res = await fetch(`/api/ms365/onboarding?token=${encodeURIComponent(token)}`);
      const body = await res.json().catch(() => ({}));

      if (!res.ok) {
        const msg = body.error || t("ms365Onboarding.errors.loadData");
        setError(msg);
        setStatus(res.status === 401 ? "token_invalid" : "token_expired");
        return;
      }

      setData(body as OnboardingData);

      if (body.admin_consent_granted && body.active_connections > 0) {
        setStatus("fully_complete");
      } else if (body.admin_consent_granted) {
        setStatus("admin_consent_done");
      } else {
        setStatus("admin_consent_pending");
      }
    } catch (err: any) {
      setError(err?.message ?? t("ms365Onboarding.errors.network"));
      setStatus("token_invalid");
    }
  }, [t, token]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  return { status, data, error, refresh };
}
